import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { StateService } from '@app/shared/services/state/state.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { Location } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { MatDialog } from '@angular/material/dialog';
import { of, forkJoin, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { getRoutePrefix } from '@app/shared/utils/route-prefix.util';
import { StoreService } from '@app/feature/store-admin/services/store.service';
import { CommonService } from '@app/shared/services/common/common.service';
import { Constants } from '@app/shared/constants';
import { resolvePublishFinishingCost } from './publish-pricing';

interface ProductLabel {
  text: string;
  backgroundColor: string;
  textColor: string;
}

interface ProductPricingData {
  productId: string;
  productName: string;
  productCategory: string;
  productImage: string;
  colorCount: number;
  basePrice: number;
  retailPrice: number;
  profitValue: number;
  profitType: 'percent' | 'dollar';
  tags: string[];
  title: string;
  description: string;
  labels: ProductLabel[];
  detailImages: File[];
  detailImagePreviews: string[];
  /** Per-additional-side decoration rate forwarded from color selection; null → fall back to printCost. */
  decorationRate: number | null;
  /** Count of designed sides (imprint locations) — drives the additional-side cost. */
  designedSideCount: number;
}

@Component({
  selector: 'app-product-pricing-tags',
  templateUrl: './product-pricing-tags.component.html',
  styleUrls: ['./product-pricing-tags.component.css']
})
export class ProductPricingTagsComponent implements OnInit {
  @ViewChild('successDialog') successDialog!: TemplateRef<any>;
  @ViewChild('storeSelectionDialog') storeSelectionDialog!: TemplateRef<any>;

  storeId!: number;
  storeName: string = '';

  colorVariantsData: any = null;
  colorVariantsPayload: any[] = []; // Final payload from color selection page
  products: ProductPricingData[] = [];

  // Store selection (store-admin flow)
  isStoreAdmin: boolean = false;
  storeList: any[] = [];
  selectedPublishStoreId: number = 0;

  // Edit mode
  isEditMode: boolean = false;
  editProductData: any = null;

  // Summary stats
  totalProducts: number = 0;
  totalVariants: number = 0;
  averagePrice: number = 0;
  uniqueTags: Set<string> = new Set();

  // Global pricing
  globalProfitValue: number = 0;
  globalProfitType: 'percent' | 'dollar' = 'dollar';

  // Pricing config from backend. No implicit finishing fee: published price = catalog price.
  finishingCost: number = 0;
  printCost: number = Constants.PrintingCostPrice;

  // Suggested tags
  suggestedTags: string[] = ['Best Seller', 'New', 'Sale', 'Premium', 'Eco-Friendly'];

  // Custom tag input for each product
  customTagInputs: { [productId: string]: string } = {};

  // Angular Editor config for description
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '120px',
    minHeight: '80px',
    placeholder: 'Enter product description...',
    translate: 'no',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    toolbarHiddenButtons: [
      ['insertImage', 'insertVideo', 'toggleEditorMode', 'customClasses', 'link', 'unlink']
    ]
  };

  // Color picker is handled by ngx-color-picker module

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private stateService: StateService,
    private notification: NotificationService,
    private loader: LoaderService,
    private location: Location,
    private http: HttpClient,
    public dialog: MatDialog,
    private storeService: StoreService,
    private commonService: CommonService
  ) {
    this.route.queryParams.subscribe(params => {
      this.storeId = params['storeId'] || localStorage.getItem('storeId');
      this.storeName = params['storeName'] || localStorage.getItem('storeName') || '';
    });
  }

  ngOnInit(): void {
    // Detect store-admin context
    const userRole = localStorage.getItem('role');
    this.isStoreAdmin = !(userRole === 'SuperAdmin' || userRole === 'CustomerService');

    // Check edit mode
    this.isEditMode = this.stateService.isEditMode();
    this.editProductData = this.stateService.getEditProductData();

    // Load color variants data
    this.colorVariantsData = this.stateService.getColorVariantsData();

    // Load color variants payload (final payload for API)
    this.colorVariantsPayload = this.stateService.getColorVariantsPayload() || [];

    if (!this.colorVariantsData || !this.colorVariantsData.products) {
      this.notification.error('No product data found. Please start over.', 'Error');
      this.goBack();
      return;
    }

    // Transform data into pricing format
    this.loadProductPricingData();
    this.calculateSummaryStats();

    // Retail price includes the finishing cost, so pull the store's own pricing config now
    // instead of waiting until publish (see loadStorePricingConfig).
    this.loadStorePricingConfig();

  }

  loadProductPricingData(): void {
    // Get existing product data for edit mode pre-fill
    const editDetails = this.isEditMode ? this.editProductData?.productDetails : null;

    this.products = this.colorVariantsData.products.map((product: any) => {
      const basePrice = this.getMinSizePrice(product);

      // Additional-side (extra imprint location) cost uses the SELECTED decoration type's rate
      // (embroidery vs digital), forwarded from the color-selection page. Mirrors the canonical
      // backend model Helper.CalculateProductCosting: (designedSides − 1) × rate.
      const designedSideCount = this.getDesignedSideCount(product);
      const decorationRate = (product.decorationRate != null) ? Number(product.decorationRate) : null;
      const extraSideCost = this.computeExtraSideCost(decorationRate, designedSideCount);

      // In edit mode, use existing profit value; otherwise use global default
      let profitValue = this.globalProfitValue;
      let profitType: 'percent' | 'dollar' = this.globalProfitType;
      let tags: string[] = product.productCategory ? [product.productCategory] : [];
      let title = '';
      let description = '';
      let labels: ProductLabel[] = [];

      if (this.isEditMode && editDetails) {
        // Use RetailPrice from locked variant as the price adjustment (profit)
        const lockedCP = (editDetails.CustomizedProducts || [])[0];
        const dbRetailPrice = lockedCP?.RetailPrice || editDetails.RetailPrice || 0;
        if (dbRetailPrice > 0) {
          profitValue = dbRetailPrice;
        } else {
          profitValue = editDetails.ShopifyProfit || editDetails.Discount || this.globalProfitValue;
        }

        // Parse existing tags (handles both JSON format and comma-separated)
        if (editDetails.Tags) {
          let existingTags: string[] = [];
          try {
            const parsedTags = JSON.parse(editDetails.Tags);
            if (Array.isArray(parsedTags)) {
              existingTags = parsedTags.map((t: any) => typeof t === 'string' ? t : (t.tagtext || '')).filter((t: string) => t);
            }
          } catch (e) {
            existingTags = editDetails.Tags.split(',').map((t: string) => t.trim()).filter((t: string) => t);
          }
          tags = existingTags.length > 0 ? existingTags : tags;
        }

        // Use updated project title if changed in dialog, otherwise fall back to DB name
        title = product.ProjectName || product.ProjectTitle || editDetails.Name || editDetails.ProductDescription?.ProductName || '';
        description = editDetails.Description || editDetails.ProductDescription?.LongDescription || '';

        // Parse existing labels from ProductMetadata (locked variant)
        const metadataSource = lockedCP?.ProductMetadata || editDetails.ProductMetadata;
        if (metadataSource) {
          try {
            const parsed = typeof metadataSource === 'string' ? JSON.parse(metadataSource) : metadataSource;
            // Handle both formats: direct array [{text,...}] or nested {labels:[...]}
            const labelArray = Array.isArray(parsed) ? parsed : (parsed?.labels || []);
            if (labelArray.length > 0) {
              labels = labelArray.map((l: any) => ({
                text: l.text || '',
                backgroundColor: l.backgroundColor || '#667eea',
                textColor: l.textColor || '#FFFFFF'
              }));
            }
          } catch (e) {
          }
        }
      }

      const retailPrice = this.calculateRetailPrice(basePrice, profitValue, profitType, extraSideCost);

      // Use ProductId (capitalized) to match with colorVariantsPayload
      const productId = product.ProductId || product.productId;

      // Use projectName from first selected color for title (fallback)
      if (!title) {
        title = product.selectedColors?.[0]?.projectName
          || product.ProjectName || product.ProjectTitle || product.productName || '';
      }

      if (!description) {
        description = product.LongDescription || product.Description || '';
      }

      return {
        productId: productId,
        productName: product.productName,
        productCategory: product.productCategory || product.CustomizationType || '',
        productImage: product.productImage,
        colorCount: product.selectedColors?.length || 0,
        basePrice: basePrice,
        retailPrice: retailPrice,
        profitValue: profitValue,
        profitType: profitType,
        tags: tags,
        title: title,
        description: description,
        labels: labels,
        decorationRate: decorationRate,
        designedSideCount: designedSideCount,
        detailImages: [],
        detailImagePreviews: []
      };
    });

  }

  /**
   * Base price for a product = the LOWEST price in its size table ("from $X").
   * Sizes live on each style as Sizes[].UnitPrice — the same field buildVariants uses to price
   * every Shopify variant — so the figure shown here is the cheapest size the product sells at.
   * Previously this read the product-level UnitCost, which ignored the size table entirely.
   *
   * Only the SELECTED colours' size tables are scanned, since unselected styles are never
   * published. Non-positive / non-numeric prices are skipped so a missing UnitPrice can't drag
   * the minimum to 0. Falls back to product.UnitCost (the previous behaviour) when the size
   * table yields no usable price.
   */
  getMinSizePrice(product: any): number {
    const selectedColors = product?.selectedColors || [];
    const availableColors = product?.availableColors || product?.Styles || [];
    let min = Number.POSITIVE_INFINITY;

    selectedColors.forEach((color: any) => {
      const style = availableColors.find((s: any) =>
        Number(s.ID) === Number(color.styleId) || Number(s.StyleId) === Number(color.styleId)
      );
      (style?.Sizes || []).forEach((size: any) => {
        const price = Number(size?.UnitPrice);
        if (Number.isFinite(price) && price > 0 && price < min) {
          min = price;
        }
      });
    });

    if (Number.isFinite(min)) return min;
    return Number(product?.UnitCost) || 0;
  }

  /**
   * Retail price = base price (lowest size price) + finishing cost + additional-side decoration
   * cost + pricing adjustment.
   *
   * finishingCost and the extra-side cost are added as FLAT per-item charges and are deliberately
   * not marked up by a percent adjustment — this mirrors how buildVariants prices the real Shopify
   * variants (unitPrice + finishingCost + extraSidesCharge + profit), so the figure shown here
   * lines up with what actually gets published.
   *
   * extraSideCost = decorationRate × (designedSides − 1) (see computeExtraSideCost). It is passed in
   * because it depends on the product's designed-side count and selected decoration type.
   */
  calculateRetailPrice(basePrice: number, profitValue: number, profitType: 'percent' | 'dollar', extraSideCost: number = 0): number {
    const finishing = Number(this.finishingCost) || 0;
    const sideCost = Number(extraSideCost) || 0;
    if (profitType === 'percent') {
      return basePrice * (1 + profitValue / 100) + finishing + sideCost;
    } else {
      return basePrice + profitValue + finishing + sideCost;
    }
  }

  /**
   * Number of decoration locations (imprint sides) the product publishes with. The reliable source
   * is each selected colour's `sides` array — these are the ACTIVE imprint locations the user picked
   * (e.g. Front + Right Sleeve). The product-level `Sides` is the full template side list and is
   * left untouched upstream (it does NOT carry the per-variant designed thumbnails), so counting it
   * under-reports to 1 — which is why a 2-location embroidery product was still priced as 1 side.
   * Returns the max distinct designed-side count across the selected colours, falling back to all
   * active variant sides, then the product-level designed sides, then 1. Shared by the Retail Price
   * shown here and buildShopifyPayload() so the displayed price equals the published price.
   */
  getDesignedSideCount(product: any): number {
    const variants = product?.selectedColors || [];
    const distinctSideNames = (sides: any[], designedOnly: boolean): number => {
      const names = new Set(
        (sides || [])
          .filter((s: any) => !designedOnly || (s && (s.ThumbnailUrl || s.ThumbnailRelativeUrl || s.DownloadUrl || s.ProjectId)))
          .map((s: any) => (s?.SideName || s?.Name || '').toString().trim().toLowerCase())
          .filter((n: string) => !!n)
      );
      return names.size;
    };

    let maxDesigned = 0;
    let maxActive = 0;
    for (const v of variants) {
      maxDesigned = Math.max(maxDesigned, distinctSideNames(v?.sides, true));
      maxActive = Math.max(maxActive, distinctSideNames(v?.sides, false));
    }
    if (maxDesigned > 0) { return maxDesigned; }
    if (maxActive > 0) { return maxActive; }

    // Fallback: product-level sides that carry a design (legacy behavior).
    const allSides = product?.Sides || [];
    const designedTop = allSides.filter((s: any) => s.DownloadUrl || s.ThumbnailUrl || s.ThumbnailRelativeUrl);
    return designedTop.length || 1;
  }

  /**
   * Additional-side decoration cost = rate × (designedSides − 1), matching the canonical backend
   * model (Helper.CalculateProductCosting). The first side's decoration is covered by finishingCost;
   * each extra imprint location adds the decoration rate. Falls back to the store's flat printCost
   * when no per-decoration rate was resolved (digital / unmapped products — unchanged behavior).
   */
  private computeExtraSideCost(decorationRate: number | null, designedSideCount: number): number {
    const rate = (decorationRate != null) ? decorationRate : (Number(this.printCost) || 0);
    return rate * Math.max((designedSideCount || 1) - 1, 0);
  }

  /** Per-row additional-side cost (used by the retail recalcs and the template breakdown). */
  getExtraSideCost(product: ProductPricingData): number {
    return this.computeExtraSideCost(product.decorationRate, product.designedSideCount);
  }

  /**
   * Fetch the store's pricing config (FinishingPrice / PrintingCost) on page load so the Retail
   * Price shown here reflects the store's real finishing cost. Without this the page would render
   * the defaults (finishing 0 / print 5) while proceedWithPublish() prices the actual
   * variants from the store's own values — the two would silently disagree.
   * On failure the Constants defaults are kept: the retail price stays an estimate rather than
   * blocking the page.
   */
  loadStorePricingConfig(): void {
    if (!this.storeId) {
      return;
    }
    this.storeService.getStoreById(String(this.storeId)).subscribe({
      next: (storeRes: any) => {
        const responseData = storeRes?.Data?.ResponseData;
        this.printCost = responseData?.PrintingCost || Constants.PrintingCostPrice;
        this.finishingCost = resolvePublishFinishingCost(responseData);
        this.recalculateAllRetailPrices();
      },
      error: () => {
        this.recalculateAllRetailPrices();
      }
    });
  }

  /** Re-derive every product's retail price after the finishing/print costs change. */
  recalculateAllRetailPrices(): void {
    this.products.forEach(product => {
      product.retailPrice = this.calculateRetailPrice(product.basePrice, product.profitValue, product.profitType, this.getExtraSideCost(product));
    });
    this.calculateSummaryStats();
  }

  calculateSummaryStats(): void {
    this.totalProducts = this.products.length;
    this.totalVariants = this.products.reduce((sum, p) => sum + p.colorCount, 0);

    if (this.products.length > 0) {
      const totalRetailPrice = this.products.reduce((sum, p) => sum + p.retailPrice, 0);
      this.averagePrice = totalRetailPrice / this.products.length;
    }

    this.uniqueTags.clear();
    this.products.forEach(p => {
      p.tags.forEach(tag => this.uniqueTags.add(tag));
    });
  }

  onProfitValueChange(product: ProductPricingData): void {
    product.retailPrice = this.calculateRetailPrice(product.basePrice, product.profitValue, product.profitType, this.getExtraSideCost(product));
    this.calculateSummaryStats();
  }

  onProfitTypeChange(product: ProductPricingData): void {
    product.retailPrice = this.calculateRetailPrice(product.basePrice, product.profitValue, product.profitType, this.getExtraSideCost(product));
    this.calculateSummaryStats();
  }

  applyGlobalPricing(): void {
    this.products.forEach(product => {
      product.profitValue = this.globalProfitValue;
      product.profitType = this.globalProfitType;
      product.retailPrice = this.calculateRetailPrice(product.basePrice, this.globalProfitValue, this.globalProfitType, this.getExtraSideCost(product));
    });
    this.calculateSummaryStats();
    this.notification.success('Global pricing applied to all products', 'Success');
  }

  addTag(product: ProductPricingData, tag: string): void {
    const trimmedTag = tag.trim();
    if (trimmedTag && !product.tags.includes(trimmedTag)) {
      product.tags.push(trimmedTag);
      this.calculateSummaryStats();
    }
  }

  removeTag(product: ProductPricingData, tag: string): void {
    if (tag === product.productCategory) return;
    const index = product.tags.indexOf(tag);
    if (index > -1) {
      product.tags.splice(index, 1);
      this.calculateSummaryStats();
    }
  }

  addCustomTag(product: ProductPricingData): void {
    const customTag = this.customTagInputs[product.productId];
    if (customTag && customTag.trim()) {
      this.addTag(product, customTag.trim());
      this.customTagInputs[product.productId] = ''; // Clear input after adding
    }
  }

  onCustomTagKeyPress(event: KeyboardEvent, product: ProductPricingData): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addCustomTag(product);
    }
  }

  // ==================== Label Methods ====================

  addLabel(product: ProductPricingData): void {
    product.labels.push({ text: '', backgroundColor: '#667eea', textColor: '#FFFFFF' });
  }

  removeLabel(product: ProductPricingData, index: number): void {
    product.labels.splice(index, 1);
  }

  // Color picker is handled by ngx-color-picker directive in the template

  // ==================== End Label Methods ====================

  hasCustomTagInput(product: ProductPricingData): boolean {
    return !!(this.customTagInputs[product.productId] && this.customTagInputs[product.productId].trim());
  }

  onDetailImageSelected(product: ProductPricingData, event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        product.detailImages.push(file);

        // Generate preview
        const reader = new FileReader();
        reader.onload = (e: any) => {
          product.detailImagePreviews.push(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  }

  removeDetailImage(product: ProductPricingData, index: number): void {
    product.detailImages.splice(index, 1);
    product.detailImagePreviews.splice(index, 1);
  }

  goBack(): void {
    this.location.back();
  }

  /**
   * WI 1335 / 1336 — TRUE SOURCE FIX. The products list renders a rich-text <angular-editor>
   * (and a title input) per row via *ngFor. Without a stable trackBy, Angular reuses the editor
   * DOM/component instances across rows, so one product's contenteditable content bleeds onto
   * others and the two-way [(ngModel)]="product.description" / "product.title" then persists the
   * wrong (another product's) value on publish — the carry-over seen across many stores.
   * Tracking by the stable productId keeps each row's editor bound to its own product.
   */
  trackByProductId(index: number, product: any): any {
    return product && product.productId != null ? product.productId : index;
  }

  publishProducts(): void {
    if (this.products.length === 0) {
      this.notification.warning('No products to publish', 'Warning');
      return;
    }

    // The title the user typed is published as-is. The WI 1335 "auto-correct mismatched titles"
    // heuristic that used to live here rewrote any title sharing no word with the catalogue
    // product name to "<prefix> - <product name>", which (a) threw away deliberate custom names
    // and (b) collided with earlier publishes of the same product under that same generated name,
    // surfacing as a 409 "Project title already exists" for a name the user never entered.
    // The carry-over bug it guarded against has its real fix in trackByProductId above.
    // Duplicate titles are still rejected by SaveHuddleProductStyles (409) and shown as an error
    // notification in proceedWithPublish — no silent rewriting.

    // Pricing adjustment removed — a profit of 0 is allowed. Publish proceeds with
    // retail price = base price (ShopifyProfit defaults to 0 downstream).

    if (this.isStoreAdmin) {
      // Store-admin: show store selection dialog first
      this.loadStoreListAndShowDialog();
    } else {
      // Super-admin: proceed directly with storeId from query params
      this.proceedWithPublish(this.storeId);
    }
  }

  /**
   * Load store list for store-admin and show store selection dialog
   * Uses same commonService.getStoreList as published-product component
   */
  loadStoreListAndShowDialog(): void {
    this.loader.show('Loading stores…', 'Please wait.');
    const userId = localStorage.getItem('userId') || '0';
    this.commonService.getStoreList('', 0, 100, true, userId, true).subscribe({
      next: (res: any) => {
        this.loader.hide();
        if (res.StatusCode === 200 && res.Data?.ResponseData) {
          this.storeList = res.Data.ResponseData;
          this.selectedPublishStoreId = 0;
          // Open store selection dialog
          this.dialog.open(this.storeSelectionDialog, {
            width: '900px',
            maxWidth: '95vw',
            disableClose: true,
            panelClass: 'store-selection-dialog'
          });
        } else {
          this.notification.error('No stores found', 'Error');
        }
      },
      error: (err) => {
        this.loader.hide();
        this.notification.error('Failed to load stores', 'Error');
      }
    });
  }

  /**
   * Called when store-admin selects a store from the dialog
   */
  onStoreSelected(storeId: number): void {
    this.selectedPublishStoreId = storeId;
  }

  /**
   * Confirm store selection and proceed with publishing (store-admin path)
   * Uses exact same API flow as super-admin: GetStoreById → SaveHuddleProductStyles → Shopify /raw → SaveStorePublishProduct
   */
  confirmStoreSelection(): void {
    if (!this.selectedPublishStoreId) {
      this.notification.warning('Please select a store', 'Warning');
      return;
    }
    this.dialog.closeAll();
    this.storeId = this.selectedPublishStoreId;

    // Patch StoreId into colorVariantsPayload (wasn't known at color-selection time for store-admin)
    this.colorVariantsPayload.forEach((p: any) => {
      p.StoreId = String(this.storeId);
    });

    // Same flow as super-admin
    this.proceedWithPublish(this.selectedPublishStoreId);
  }

  /**
   * Fetch API key then run the full publish chain
   * Uses same storeService.getStoreById as published-product component
   * Used by BOTH super-admin and store-admin flows (exact same path)
   */
  proceedWithPublish(publishStoreId: number): void {
    this.loader.show('Publishing your product to Shopify…', 'Preparing store details. This may take a moment.');

    // Update storeId for downstream calls
    this.storeId = publishStoreId;

    // Same service call as published-product component (storeService.getStoreById)
    this.storeService.getStoreById(String(publishStoreId)).subscribe({
      next: (storeRes: any) => {
        const responseData = storeRes?.Data?.ResponseData;

        const apiKey = responseData?.apikey || responseData?.ApiKey || '';

        // Fetch pricing config from store
        this.printCost = responseData?.PrintingCost || Constants.PrintingCostPrice;
        this.finishingCost = resolvePublishFinishingCost(responseData);

        if (!apiKey) {
          this.notification.error('Could not retrieve Shopify API key for this store. Please ensure the store has Shopify connected.', 'Error');
          this.loader.hide();
          return;
        }

        this.saveAndPublishToShopify(apiKey);
      },
      error: (err) => {
        this.notification.error('Failed to fetch store details', 'Error');
        this.loader.hide();
      }
    });
  }

  /**
   * Save product styles to backend (with title, description, labels, tags, profit) then publish to Shopify
   */
  saveAndPublishToShopify(apiKey: string): void {
    // Merge title, description, labels, tags, profit into each colorVariantsPayload entry
    const backendPayload = this.colorVariantsPayload.map((variantPayload: any) => {
      const pricingData = this.products.find(p =>
        String(p.productId) === String(variantPayload.ProductId)
      );
      const tagsArray = pricingData?.tags?.map(tag => ({ tagtext: tag })) || [];

      const labelsFiltered = (pricingData?.labels || []).filter(l => l.text.trim()).map(l => ({
        Text: l.text,
        BackgroundColor: l.backgroundColor,
        TextColor: l.textColor
      }));
      const updatedTitle = pricingData?.title || '';
      const payload: any = {
        ...variantPayload,
        ShopifyProfit: pricingData?.profitValue || 0,
        Tags: tagsArray,
        Title: updatedTitle,
        // Keep ProjectName in sync with Title so backend duplicate check uses the current title
        ProjectName: updatedTitle || variantPayload.ProjectName,
        Description: pricingData?.description || '',
        Labels: labelsFiltered
      };

      // In edit mode, pass OldDesignId and OldProjectName so backend soft-deletes old records then creates new
      if (this.isEditMode && this.editProductData) {
        payload.IsUpdate = true;
        payload.OldDesignId = this.editProductData.DesignId;
        payload.OldProjectName = this.editProductData.productDetails?.Name || '';
      }

      return payload;
    });

    const designIdMap: { [productId: string]: number } = {};
    const customizedProductIdMap: { [productId: string]: number } = {};

    // --- Loader: Save phase ---
    const productCount = backendPayload.length;
    const saveLabel = `Saving ${productCount} ${productCount === 1 ? 'product' : 'products'}`;
    this.loader.setPersistentMessage(saveLabel);

    // Save to our backend using SaveHuddleProductStyles
    // All products are sent (including InkSoft variants with IsInksoft=true) so pricing/tags update in DB
    this.http.post(`${environment.baseUrl}/api/DesignHuddle/SaveHuddleProductStyles`, backendPayload).subscribe({
      next: (saveResponse: any) => {

        // Check for duplicate title error from backend
        if (saveResponse?.StatusCode === 409) {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          this.notification.error(saveResponse.Message || 'Project title already exists. Please choose a different name.', 'Error');
          return;
        }

        // Extract DesignId and CustomizedProductId mappings from response
        const savedProducts = saveResponse?.Data?.ResponseData?.SavedProducts || [];
        savedProducts.forEach((sp: any) => {
          if (sp.ProductId) {
            if (sp.DesignId) designIdMap[String(sp.ProductId)] = sp.DesignId;
            if (sp.CustomizedProductId) customizedProductIdMap[String(sp.ProductId)] = sp.CustomizedProductId;
          }
        });

        // In edit mode, fall back to existing DesignId for any product not in the save response
        // (e.g. products whose variants were all skipped due to no migration)
        if (this.isEditMode && this.editProductData) {
          const pid = String(this.editProductData.ProductId);
          if (!designIdMap[pid] && this.editProductData.DesignId) {
            designIdMap[pid] = this.editProductData.DesignId;
          }
          // Do NOT fallback to old CustomizedProductId — old records are soft-deleted, new ones created by save
        }

        // PUBLISH GUARD (work item #1170): the backend returns HTTP 200 even when
        // individual saves fail — per-product failures only appear in the Errors
        // array. Publishing a product that has no MerchLoop record creates a live,
        // sellable Shopify product with no DB lineage (unsearchable, uneditable,
        // unfulfillable), so Shopify must never be contacted unless every product
        // in this batch has a resolved CustomizedProductId.
        const saveErrors: any[] = saveResponse?.Data?.ResponseData?.Errors || [];
        const missingIds = backendPayload
          .map((p: any) => String(p.ProductId))
          .filter((pid: string) => !customizedProductIdMap[pid]);
        if (saveErrors.length > 0 || savedProducts.length === 0 || missingIds.length > 0) {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          console.error('Publish aborted: MerchLoop save incomplete.', { saveErrors, missingIds });
          const failedCount = missingIds.length || backendPayload.length;
          // The API's reasons (e.g. a colour that needs its own print file) tell the user what to fix.
          const reasons = saveErrors.filter((e: any) => typeof e === 'string' && e.trim()).join(' ');
          this.notification.error(
            `Publishing cancelled: ${failedCount} of ${backendPayload.length} product(s) could not be saved in MerchLoop, so nothing was ${this.isEditMode ? 'updated in' : 'published to'} Shopify. ` +
            (reasons ? reasons : 'Please try again or contact support if the problem continues.'),
            'Error'
          );
          return;
        }

        // DESIGN-ID GUARD (WI 1337-followup / MLP_0_ duplicates): the Shopify SKU is
        // MLP_{designId}_{uniqueSizeId}. If a product's DesignId didn't resolve here, the payload
        // falls back to productid=0 (buildShopifyPayload) and the node bulk endpoint would publish a
        // broken/duplicate product with an MLP_0_ SKU. Never contact Shopify unless EVERY product has
        // a real (non-zero) DesignId resolved. The design already exists in MerchLoop at this point —
        // this only prevents a 0 from leaking into the Shopify SKU.
        const missingDesignIds = backendPayload
          .map((p: any) => String(p.ProductId))
          .filter((pid: string) => !designIdMap[pid] || Number(designIdMap[pid]) === 0);
        if (missingDesignIds.length > 0) {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          console.error('Publish aborted: unresolved DesignId (would create MLP_0_ SKUs).', { missingDesignIds });
          this.notification.error(
            `Publishing cancelled: ${missingDesignIds.length} of ${backendPayload.length} product(s) did not return a valid design id, so nothing was ${this.isEditMode ? 'updated in' : 'published to'} Shopify (this prevents broken MLP_0_ SKUs). Please try again or contact support if the problem continues.`,
            'Error'
          );
          return;
        }

        if (this.isEditMode) {
          // Update flow: update existing Shopify product
          this.updateShopifyProduct(apiKey, designIdMap, customizedProductIdMap);
        } else {
          // Create flow: publish new product to Shopify
          this.publishToShopify(apiKey, designIdMap, customizedProductIdMap);
        }
      },
      error: (error: any) => {
        this.loader.clearPersistentMessage();
        this.loader.hide();
        this.notification.error('Failed to save products', 'Error');
      }
    });
  }

  /**
   * Update existing Shopify product (edit mode)
   */
  updateShopifyProduct(apiKey: string, designIdMap: { [productId: string]: number } = {}, customizedProductIdMap: { [productId: string]: number } = {}): void {
    const productsList = this.colorVariantsData.products;
    const oldDesignId = this.editProductData?.DesignId || 0;
    const productIds = productsList.map((p: any) => String(p.ProductId || p.productId));

    this.fetchSizeChartHtmlMap(productIds).subscribe(sizeChartMap => {
      // Build Shopify payloads for update (same structure as create, but sent to update endpoint)
      const shopifyPayloads = productsList.map((product: any) => {
        const pricingData = this.products.find(p =>
          String(p.productId) === String(product.ProductId || product.productId)
        );
        const productId = String(product.ProductId || product.productId);
        const payload = this.buildShopifyPayload(product, pricingData, apiKey, designIdMap, sizeChartMap[productId] || '');

        // productid = new DesignId, productid_old = old DesignId (for Shopify to find existing product)
        payload.productid = designIdMap[productId] || payload.productid;
        payload.productid_old = oldDesignId;
        // StoreId as number to match update payload format
        payload.StoreId = Number(this.storeId);
        // Remove apiKey from payload (not needed in update)
        delete payload.apiKey;

        return payload;
      });

      // --- Loader: transition from save phase to publish/update phase ---
      const updateCount = shopifyPayloads.length;
      this.loader.setPersistentMessage(`Publishing ${updateCount} ${updateCount === 1 ? 'product' : 'products'} to Shopify`);

      // Use /api/product/update endpoint for edit mode
      const headers = new HttpHeaders().set('x-api-key', apiKey);
      const updateCalls: Observable<any>[] = shopifyPayloads.map((payload: any) =>
        this.http.post<any>(
          `${environment.baseUrlShopifyApp}/api/product/update`,
          payload,
          { headers }
        ).pipe(
          catchError(error => {
            return of({ error: true, message: error.message });
          })
        )
      );

      forkJoin(updateCalls).subscribe({
        next: (results: any[]) => {
          const failures = results.filter(r => r?.error);
          if (failures.length === 0) {

            // Update label metafields for each product
            shopifyPayloads.forEach((payload: any) => {
              const productId = String(payload.Product_Id || payload.productid);
              const pricingData = this.products.find(p => String(p.productId) === productId);
              const labelsForMetafield = (pricingData?.labels || [])
                .filter((l: any) => l.text.trim())
                .map((l: any) => ({ text: l.text, backgroundColor: l.backgroundColor, textColor: l.textColor }));

              if (labelsForMetafield.length > 0) {
                const metafieldPayload = {
                  productId: designIdMap[productId] || payload.productid,
                  key: 'product_label',
                  type: 'json',
                  namespace: 'custom',
                  value: labelsForMetafield,
                  apiKey: apiKey
                };
                this.http.post(
                  `${environment.baseUrlShopifyApp}/api/product/addupdatemetafields`,
                  metafieldPayload,
                  { headers }
                ).subscribe({
                  next: () => {},
                  error: () => {}
                });
              }
            });

            // Create new store publish mappings with new CustomizedProductId (old ones reference soft-deleted records)
            this.createStorePublishMappings(customizedProductIdMap);
          } else {
            this.loader.clearPersistentMessage();
            this.loader.hide();
            this.notification.error(`${failures.length} product(s) failed to update in Shopify`, 'Error');
          }
        },
        error: (error: any) => {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          this.notification.error('Failed to update products in Shopify', 'Error');
        }
      });
    });
  }

  /**
   * Update existing store publish mappings (edit mode).
   * Sends all products in a single batch POST to /Store/SaveStorePublishProducts.
   */
  updateStorePublishMappings(customizedProductIdMap: { [productId: string]: number }): void {
    const products = this.buildPublishProductItems(customizedProductIdMap);

    if (products.length === 0) {
      // Work item #1170: no saved products — do not report a successful update.
      this.loader.clearPersistentMessage();
      this.loader.hide();
      this.notification.error('Update could not be completed because no saved products were found in MerchLoop. Please verify the product before selling it.', 'Error');
      return;
    }

    const batchPayload = { StoreId: Number(this.storeId), Products: products };

    this.http.post<any>(`${environment.baseUrl}/Store/SaveStorePublishProducts`, batchPayload)
      .pipe(catchError(error => of({ error: true })))
      .subscribe({
        next: () => {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          this.onUpdateComplete();
        },
        error: () => {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          this.notification.error('Failed to update publish status', 'Error');
        }
      });
  }

  /**
   * Handle completion of update flow
   */
  onUpdateComplete(): void {
    // Clear edit mode state
    this.stateService.clearEditMode();

    this.showSuccessDialog();
    this.notification.success('Product updated successfully!', 'Success');
  }

  /**
   * Build Shopify product/raw payloads and publish all products to Shopify in one bulk call.
   * Sends { products: [...] } to /api/product/raw/bulk instead of N individual /api/product/raw calls.
   * Bulk response shape handled defensively: supports { products:[...] }, { data:[...] }, or raw array.
   */
  publishToShopify(apiKey: string, designIdMap: { [productId: string]: number } = {}, customizedProductIdMap: { [productId: string]: number } = {}): void {
    const productsList = this.colorVariantsData.products;
    const productIds = productsList.map((p: any) => String(p.ProductId || p.productId));

    this.fetchSizeChartHtmlMap(productIds).subscribe(sizeChartMap => {
      // Build a Shopify payload for each product (identical object that was previously sent per single /raw call)
      const shopifyPayloads = productsList.map((product: any) => {
        const pricingData = this.products.find(p =>
          String(p.productId) === String(product.ProductId || product.productId)
        );
        const productId = String(product.ProductId || product.productId);
        return this.buildShopifyPayload(product, pricingData, apiKey, designIdMap, sizeChartMap[productId] || '');
      });

      // --- Loader: transition from save phase to publish phase ---
      const publishCount = shopifyPayloads.length;
      this.loader.setPersistentMessage(`Publishing ${publishCount} ${publishCount === 1 ? 'product' : 'products'} to Shopify`);

      // Single bulk POST — all products in one request
      const headers = new HttpHeaders().set('x-api-key', apiKey);
      this.http.post<any>(
        `${environment.baseUrlShopifyApp}/api/product/raw/bulk`,
        { products: shopifyPayloads },
        { headers }
      ).subscribe({
        next: (bulkResponse: any) => {
          // Defensive response unwrapping: backend may return { products:[...] }, { data:[...] }, or a raw array
          const responseArray: any[] = Array.isArray(bulkResponse)
            ? bulkResponse
            : (bulkResponse?.products || bulkResponse?.data || []);

          const failures = responseArray.filter((r: any) => r?.error);
          if (failures.length === 0) {
            // Create StorePublishProductMapping for each product (marks IsPublished=true)
            this.createStorePublishMappings(customizedProductIdMap);
          } else {
            this.loader.clearPersistentMessage();
            this.loader.hide();
            this.notification.error(`${failures.length} product(s) failed to publish to Shopify`, 'Error');
          }
        },
        error: (error: any) => {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          this.notification.error('Failed to publish products to Shopify', 'Error');
        }
      });
    });
  }

  /**
   * Build Shopify product/raw JSON payload for a single product
   * Matches the same structure as add-product-popup's mapShopifyRequestDataForAddProduct()
   */
  buildShopifyPayload(product: any, pricingData: ProductPricingData | undefined, apiKey: string, designIdMap: { [productId: string]: number } = {}, sizeChartHtml: string = ''): any {
    const selectedColors = product.selectedColors || [];
    const availableColors = product.availableColors || product.Styles || [];
    const profitValue = pricingData?.profitValue || 0;

    // Only count sides that have a design applied — shared with the Retail Price shown on this page.
    const sideCount = this.getDesignedSideCount(product);

    // Per-additional-side decoration rate (embroidery vs digital). Resolved on the color-selection
    // page and carried on pricingData/product; falls back to the store's flat printCost when absent.
    const decorationRate = (pricingData?.decorationRate != null)
      ? Number(pricingData.decorationRate)
      : this.printCost;


    // Get sizes from the first selected style (all styles have the same sizes)
    const firstSelectedStyle = availableColors.find((s: any) =>
      selectedColors.some((c: any) => c.styleId === s.ID)
    );
    const sizes = firstSelectedStyle?.Sizes || [];

    // Build Color option values (unique selected color names)
    const colorNames = [...new Set(selectedColors.map((c: any) => c.colorName))] as string[];

    // Build Size option values
    const sizeNames = sizes.map((s: any) => s.Name);

    // Build variants (color x size combinations with prices)
    const variants = this.buildVariants(selectedColors, availableColors, sizes, profitValue, sideCount, decorationRate);

    // Build VariantImages from colorVariantsPayload side thumbnails
    const variantImages = this.buildVariantImages(product, selectedColors, availableColors);

    // Main product image (first side thumbnail of first variant, or preview image)
    const mainImage = this.getMainProductImage(product, selectedColors);

    // Tags as comma-separated string or null
    const tags = pricingData?.tags?.length ? pricingData.tags.join(',') : null;

    // Use editable title & description from pricing page
    let bodyHtml = pricingData?.description || product.LongDescription || product.Description || product.productName || '';
    const productTitle = pricingData?.title || product.productName || product.Description || '';

    // Append the "Size chart" table (legacy InkSoft publish flow always included this —
    // see add-product-popup's getProductDescription()). Guarded so republishing a product
    // whose saved description already carries the table from a prior publish doesn't duplicate it.
    if (sizeChartHtml && !/size chart/i.test(bodyHtml)) {
      bodyHtml += sizeChartHtml;
    }

    // Metafields (product labels) or null
    const metafields = this.buildMetafields(pricingData);

    // Labels as JSON string for Shopify API
    const labelsForShopify = (pricingData?.labels || [])
      .filter(l => l.text.trim())
      .map(l => ({ text: l.text, backgroundColor: l.backgroundColor, textColor: l.textColor }));

    return {
      product: {
        title: productTitle,
        body_html: bodyHtml,
        vendor: 'Merchloop',
        product_type: product.productCategory || product.CustomizationType || 'Clothes',
        tags: tags,
        metafields: metafields.length > 0 ? metafields : null,
        options: [
          { name: 'Color', values: colorNames },
          { name: 'Size', values: sizeNames }
        ],
        variants: variants,
        images: mainImage ? [{ src: mainImage }] : []
      },
      productid: designIdMap[String(product.ProductId || product.productId)] || Number(product.DesignId) || 0,
      Product_Id: Number(product.ProductId || product.productId) || 0,
      StoreId: Number(this.storeId),
      apiKey: apiKey,
      VariantImages: variantImages,
      Labels: labelsForShopify.length > 0 ? labelsForShopify : ''
    };
  }

  /**
   * Fetch each product's saved size guide (Width/Length/SleeveLength per size) and build the
   * "Size chart" HTML table for it, keyed by productId. A product with no saved size guide
   * entries (or a failed fetch) maps to '' so buildShopifyPayload() simply appends nothing.
   */
  private fetchSizeChartHtmlMap(productIds: string[]): Observable<{ [productId: string]: string }> {
    const uniqueIds = [...new Set(productIds)].filter(id => id && id !== 'undefined' && id !== 'null');
    if (uniqueIds.length === 0) {
      return of({});
    }

    // `id` here is Product.UniqueID (that's what this publish flow carries under "ProductId"
    // end-to-end from master-product-listing onward), not the EF ProductId primary key - use
    // the by-unique-id endpoint so the lookup actually resolves.
    const calls = uniqueIds.map(id =>
      this.http.get<any>(`${environment.baseUrl}/api/product-management/products/by-unique-id/${id}/size-guides`).pipe(
        catchError(() => of(null))
      )
    );

    return forkJoin(calls).pipe(
      map((results: any[]) => {
        const chartMap: { [productId: string]: string } = {};
        uniqueIds.forEach((id, i) => {
          const sizeGuides = results[i]?.Data?.ResponseData || [];
          chartMap[id] = this.buildSizeChartHtml(sizeGuides);
        });
        return chartMap;
      })
    );
  }

  /**
   * Build the same "Size chart:" HTML table the legacy InkSoft publish flow embeds in body_html
   * (see add-product-popup.component.ts getProductDescription()), so pre- and post-Design-Huddle
   * products render identically on the storefront. Skeleton entries (Id === 0, no real
   * measurements saved yet) are excluded — a product with no configured size guide gets no table,
   * same as before.
   */
  private buildSizeChartHtml(sizeGuides: any[]): string {
    const realGuides = (sizeGuides || []).filter((sg: any) => sg.Id > 0);
    if (realGuides.length === 0) {
      return '';
    }

    const tableRows = realGuides.map((item: any) => `<tr>
                  <td><strong>${item.SizeName}</strong></td>
                  <td>${item.Width}</td>
                  <td>${item.Length}</td>
                  <td>${item.SleeveLength}</td>
                  </tr>`).join('');

    return `<br><p><strong>Size chart:</strong></p><div class="table-responsive dynamic" data-unit-system="imperial">
                <table cellpadding="5">
                <tbody><tr>
                <td>&nbsp;</td>
                <td><strong>W (in)</strong></td>
                <td><strong>L (in)</strong></td>
                <td><strong>SL (in)</strong></td>
                </tr> ${tableRows}</tbody></table>
                <small style="font-size:10px"><strong>Note</strong><span style="color: red">*</span>: W (Width), L (Length), SL (Sleeve length)</small>
                </div>
                `;
  }

  /**
   * Build Shopify variants array: each selected color x each size with calculated prices
   */
  buildVariants(
    selectedColors: any[],
    availableColors: any[],
    defaultSizes: any[],
    profitValue: number,
    sideCount: number,
    decorationRate?: number
  ): any[] {
    const variants: any[] = [];
    const finishingCost = this.finishingCost;
    const printCost = this.printCost;
    // Per-additional-side rate: the selected decoration type's rate when provided (embroidery vs
    // digital), else the store's flat printCost (preserves prior behavior for digital/unmapped).
    const sideRate = (decorationRate != null) ? decorationRate : printCost;

    // Log first style/size to verify property names
    if (availableColors.length > 0) {
      if (availableColors[0].Sizes?.length > 0) {
      }
    }

    selectedColors.forEach((color: any) => {
      // Match style using styleId (set from style.ID in color-selection page)
      const style = availableColors.find((s: any) =>
        s.ID === color.styleId || s.StyleId === color.styleId
      );
      if (!style) {
        return;
      }

      // Use sizes from this style (or fallback to default sizes)
      const styleSizes = style.Sizes || defaultSizes;

      styleSizes.forEach((size: any) => {
        const unitPrice = size.UnitPrice || 0;
        const extraSidesCharge = sideRate * Math.max(sideCount - 1, 0);

        // Price = unitPrice + finishingCost + (decorationRate * (sideCount - 1)) + profit
        const price = unitPrice + finishingCost + extraSidesCharge + profitValue;

        // Cost = price without profit
        const cost = unitPrice;


        // Use StyleId/UniqueStyleId from ML backend, fallback to ID (legacy)
        const styleId = Number(style.StyleId || style.UniqueStyleId || style.ID || 0);
        // Use SizeId/UniqueSizeId from ML backend, fallback to ID (legacy)
        const sizeId = Number(size.SizeId || size.UniqueSizeId || size.ID || 0);

        variants.push({
          option1: color.colorName,
          option2: size.Name,
          inventory_policy: 'deny',
          price: price.toFixed(2),
          cost: cost.toFixed(2),
          uniqueStyleId: styleId,
          uniqueSizeId: sizeId
        });
      });
    });

    return variants;
  }

  /**
   * Build VariantImages array: per-color images from side thumbnails
   */
  buildVariantImages(product: any, selectedColors: any[], availableColors: any[]): any[] {
    const variantImages: any[] = [];

    selectedColors.forEach((color: any) => {
      // Find the product payload, then find the matching style within its Styles array
      const productPayload = this.colorVariantsPayload.find((p: any) =>
        String(p.ProductId) === String(product.ProductId || product.productId)
      );

      const stylePayload = productPayload?.Styles?.find((s: any) =>
        Number(s.StyleId) === Number(color.styleId)
      );

      const images: string[] = [];

      // Get images from style's Sides ThumbnailUrl (Azure blob URLs from DesignHuddle previews)
      if (stylePayload?.Sides) {
        stylePayload.Sides.forEach((side: any) => {
          if (side.ThumbnailUrl) {
            images.push(side.ThumbnailUrl);
          }
        });
      }

      if (images.length > 0 && !variantImages.find(v => v.StyleName === color.colorName)) {
        variantImages.push({
          StyleName: color.colorName,
          Images: images
        });
      }
    });

    return variantImages;
  }

  /**
   * Get main product image URL (first side thumbnail of first selected variant)
   */
  getMainProductImage(product: any, selectedColors: any[]): string {
    // Try to get from first variant's side thumbnails (Azure blob URLs)
    if (selectedColors.length > 0) {
      const firstColor = selectedColors[0];
      const productPayload = this.colorVariantsPayload.find((p: any) =>
        String(p.ProductId) === String(product.ProductId || product.productId)
      );

      const stylePayload = productPayload?.Styles?.find((s: any) =>
        Number(s.StyleId) === Number(firstColor.styleId)
      );

      if (stylePayload?.Sides) {
        const frontSide = stylePayload.Sides.find((s: any) =>
          (s.SideName || '').toLowerCase().includes('front')
        ) || stylePayload.Sides[0];

        if (frontSide?.ThumbnailUrl) {
          return frontSide.ThumbnailUrl;
        }
      }
    }

    // Fallback to product preview image
    return product.productImage || product.PreviewImageUrl || product.ImageUrl || '';
  }

  /**
   * Build metafields array for Shopify product labels
   */
  buildMetafields(pricingData: ProductPricingData | undefined): any[] {
    if (!pricingData?.labels || pricingData.labels.length === 0) {
      return [];
    }

    const labelsJson = pricingData.labels
      .filter(l => l.text.trim())
      .map(l => ({
        text: l.text,
        backgroundColor: l.backgroundColor,
        textColor: l.textColor
      }));

    if (labelsJson.length === 0) return [];

    return [{
      key: 'product_label',
      type: 'json',
      namespace: 'custom',
      value: labelsJson
    }];
  }

  /**
   * Create StorePublishProductMapping for each published product.
   * Sends all products in a single batch POST to /Store/SaveStorePublishProducts.
   * This sets IsPublished=true and creates the mapping record so the product
   * appears on the shopify-products page.
   */
  createStorePublishMappings(customizedProductIdMap: { [productId: string]: number }): void {
    const products = this.buildPublishProductItems(customizedProductIdMap);

    if (products.length === 0) {
      // Work item #1170: reaching here with no saved products means the MerchLoop
      // records are missing — this must never be reported as a successful publish
      // (this exact branch used to show the false "published successfully!" toast).
      this.loader.clearPersistentMessage();
      this.loader.hide();
      this.notification.error('Publish status could not be recorded because no saved products were found in MerchLoop. Please verify the product appears in the products list before selling it.', 'Error');
      return;
    }

    const batchPayload = { StoreId: Number(this.storeId), Products: products };

    this.http.post<any>(`${environment.baseUrl}/Store/SaveStorePublishProducts`, batchPayload)
      .pipe(catchError(error => of({ error: true })))
      .subscribe({
        next: (response: any) => {
          this.loader.clearPersistentMessage();
          this.loader.hide();

          const results: any[] = response?.Data?.ResponseData || [];
          const failures = results.filter((r: any) => r?.Success === false);

          if (failures.length === 0) {
            if (this.isEditMode) this.stateService.clearEditMode();
            this.showSuccessDialog();
            this.notification.success(this.isEditMode ? 'Product updated successfully!' : 'Products published successfully!', 'Success');
          } else {
            this.notification.warning(
              this.isEditMode
                ? `Product updated in Shopify but ${failures.length} failed to save publish status`
                : `Products published to Shopify but ${failures.length} failed to save publish status`,
              'Warning'
            );
          }
        },
        error: () => {
          this.loader.clearPersistentMessage();
          this.loader.hide();
          this.notification.error('Failed to save publish status', 'Error');
        }
      });
  }

  /**
   * Build the per-product items array for the batch publish payload.
   * Shared by createStorePublishMappings and updateStorePublishMappings.
   */
  buildPublishProductItems(customizedProductIdMap: { [productId: string]: number }): any[] {
    return Object.entries(customizedProductIdMap).map(([productId, customizedProductId]) => {
      const pricingData = this.products.find(p => String(p.productId) === productId);
      const variantPayload = this.colorVariantsPayload.find((p: any) => String(p.ProductId) === productId);
      const userId = variantPayload?.UserId || 0;

      const tags = pricingData?.tags?.join(',') || '';
      const labelsJson = pricingData?.labels?.filter(l => l.text.trim()).map(l => ({
        text: l.text,
        backgroundColor: l.backgroundColor,
        textColor: l.textColor
      })) || [];

      return {
        CustomizedProductId: customizedProductId,
        WarehouseProductId: 0,
        StoreAdminId: userId,
        Profit: pricingData?.profitValue || 0,
        CreatedBy: userId,
        Tags: tags,
        Labels: labelsJson.length > 0 ? JSON.stringify(labelsJson) : ''
      };
    });
  }

  /**
   * Show success dialog after publishing products
   */
  showSuccessDialog(): void {
    this.dialog.open(this.successDialog, {
      width: '400px',
      disableClose: true,
      panelClass: 'success-dialog'
    });
  }

  /**
   * Close success dialog and navigate
   */
  closeSuccessDialog(): void {
    this.dialog.closeAll();
    this.stateService.clearSelectedProductsForDesign();
    this.stateService.clearSelectedProductsForColors();
    this.stateService.clearColorVariantsData();
    this.stateService.clearColorVariantsPayload();
    this.stateService.clearDesignData();
    this.stateService.clearDesignDataForProduct();
    this.stateService.clearStagedColorwayState();

    const prefix = getRoutePrefix(this.router.url);
    if (prefix === '/store') {
      this.router.navigate(['/store/customizedproducts']);
    } else {
      this.router.navigate([`/superadmin/shopify-products/${this.storeId}/${this.storeName}`]);
    }
  }
}
